import type { EventTicketSummary } from '@pulsify/shared/dto'
import { Link } from '@tanstack/react-router'
import { Pencil } from 'lucide-react'
import { formatMoney } from '~/entities/ticket/lib/ticket-earnings'
import { EVENT_TICKETS_ANCHOR } from '~/features/events/tickets/event-tickets-card'
import * as m from '~/paraglide/messages'
import { cn } from '~/shared/lib/utils'
import { Badge } from '~/shared/ui/badge'
import { Button } from '~/shared/ui/button'
import { Card } from '~/shared/ui/card'
import { countLabel } from '../lib/order-format'
import { fillRatio } from '../lib/ticket-figures'
import { ProgressBar } from './order-parts'

const TYPE_COLUMNS = 'md:grid-cols-[minmax(0,1fr)_7rem_9rem_6rem_7rem]'
const FREE_TYPE_COLUMNS = 'md:grid-cols-[minmax(0,1fr)_7rem_9rem_6rem]'

export function TicketTypeBreakdown({
  summary,
  eventId,
  locale,
}: {
  summary: EventTicketSummary
  eventId: string
  locale: string
}) {
  const money = (cents: number) => formatMoney(cents, summary.currency, locale)
  const count = (value: number) => countLabel(value, locale)
  const columns = summary.sellsPaid ? TYPE_COLUMNS : FREE_TYPE_COLUMNS
  const priceOf = (type: EventTicketSummary['types'][number]) =>
    type.priceCents > 0
      ? money(type.priceCents)
      : type.doorPriceCents
        ? m.ticketsFreeWithDoorPrice({ amount: money(type.doorPriceCents) })
        : m.free()

  return (
    <Card className="overflow-hidden">
      <div className="flex items-center justify-between gap-3 border-b px-4 py-2.5 sm:px-5">
        <h2 className="font-medium text-foreground text-sm">{m.ticketPageTypesTitle()}</h2>
        <Button asChild variant="ghost" size="sm" className="-mr-2">
          <Link to="/dashboard/events/$eventId" params={{ eventId }} hash={EVENT_TICKETS_ANCHOR}>
            <Pencil />
            {m.ticketPageTypesEdit()}
          </Link>
        </Button>
      </div>
      <div
        aria-hidden
        className={cn(
          'hidden gap-4 border-b px-5 py-2 font-medium text-muted-foreground text-xs md:grid',
          columns,
        )}
      >
        <span>{m.ticketPageColumnType()}</span>
        <span className="text-right">{m.ticketPageColumnPrice()}</span>
        <span>{summary.sellsPaid ? m.ticketPageSold() : m.ticketPageTaken()}</span>
        <span className="text-right">{m.ticketPageCheckedIn()}</span>
        {summary.sellsPaid ? <span className="text-right">{m.earningsGross()}</span> : null}
      </div>
      <ul className="divide-y divide-border">
        {summary.types.map((type) => {
          const ratio = fillRatio(type.issued, type.quantity)
          return (
            <li
              key={type.id}
              className={cn(
                'grid grid-cols-2 gap-x-4 gap-y-2 px-4 py-3 sm:px-5 md:items-center',
                columns,
              )}
            >
              <div className="col-span-2 flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 md:col-span-1">
                <p className="truncate font-medium text-foreground text-sm">{type.name}</p>
                {type.isStudent ? (
                  <Badge variant="secondary">{m.ticketsStudentBadge()}</Badge>
                ) : null}
                {type.active ? null : <Badge variant="outline">{m.ticketsOffSale()}</Badge>}
              </div>
              <TypeFigure label={m.ticketPageColumnPrice()} value={priceOf(type)} alignEnd />
              <div className="min-w-0">
                <p className="truncate text-muted-foreground text-xs md:sr-only">
                  {summary.sellsPaid ? m.ticketPageSold() : m.ticketPageTaken()}
                </p>
                <p className="truncate text-foreground text-sm tabular-nums">
                  {type.quantity === null
                    ? count(type.issued)
                    : m.ticketPageSoldOf({ sold: count(type.issued), total: count(type.quantity) })}
                </p>
                {ratio === null ? null : (
                  <div className="mt-1.5 max-w-32">
                    <ProgressBar ratio={ratio} label={type.name} />
                  </div>
                )}
              </div>
              <TypeFigure label={m.ticketPageCheckedIn()} value={count(type.checkedIn)} alignEnd />
              {summary.sellsPaid ? (
                <TypeFigure label={m.earningsGross()} value={money(type.grossCents)} alignEnd />
              ) : null}
            </li>
          )
        })}
      </ul>
    </Card>
  )
}

function TypeFigure({
  label,
  value,
  alignEnd = false,
}: {
  label: string
  value: string
  alignEnd?: boolean
}) {
  return (
    <div className={cn('min-w-0', alignEnd && 'md:text-right')}>
      <p className="truncate text-muted-foreground text-xs md:sr-only">{label}</p>
      <p className="truncate text-foreground text-sm tabular-nums">{value}</p>
    </div>
  )
}
